import { useEffect, useState, type MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { StatusPill } from '@/components/ui/status-pill';
import { Typography } from '@/components/ui/typography';
import { Variants } from '@/types/common';
import { showAlert } from '../../../../slice/AlertSlice';
import { getSyncLogsAPI } from '../../../pms/service/PMSVendorsApiService';
import { testVendorConnectionSyncAPI } from '../../../pms/pms-configuration/service/configurationApiService';
import { PMSVendorData } from '@/types/pms.types';
import type { PmsAccountRef } from '@/components/pms/services/pmsAccount';
import {
  type ConnectionState,
  connectedConnection,
  failedConnection,
} from '../../../pms/pms-configuration/types/connection-state';

interface UserAppfolioProps {
  /** DV-3345: the account every PMS call here names. */
  account: PmsAccountRef;
  connectionState: ConnectionState;
  setConnectionState: React.Dispatch<React.SetStateAction<ConnectionState>>;
  setToggleLoader: (val: boolean) => void;
  autoSyncAssets: boolean;
  watermarkedPhotos: boolean;
  setVenderListData: (data: PMSVendorData) => void;
  pmsSource: string;
  formSubmit: boolean;
  redirectToHowToConfigure: (val: boolean) => void;
  initialVendorData?: PMSVendorData | null;
  /** The unsaved-changes bar's save reaches this (form-less) view through here. */
  submitRef: MutableRefObject<(() => void) | null>;
  onSaveStored: () => void;
  /** AppFolio Passport sign-in, opened by the "Connect via AppFolio" CTA. */
  passportUrl: string;
}

const UserAppfolio = ({
  account,
  connectionState,
  setConnectionState,
  setToggleLoader,
  autoSyncAssets,
  watermarkedPhotos,
  setVenderListData,
  pmsSource,
  formSubmit,
  redirectToHowToConfigure,
  initialVendorData,
  submitRef,
  onSaveStored,
  passportUrl,
}: UserAppfolioProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [lastSyncDate, setLastSyncDate] = useState<string>('-----');
  const [intEnableDate, setIntEnableDate] = useState<string>('-----');

  useEffect(() => {
    getPMSVendorsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPMSVendorsList = async () => {
    setLoading(true);
    setToggleLoader(true);
    try {
      if (initialVendorData) {
        setVenderListData(initialVendorData);
        try {
          const syncData = await getSyncLogsAPI(account);
          if (syncData.data && syncData.data.endDate) {
            setLastSyncDate(
              dayjs(new Date(syncData.data.endDate * 1000)).format('MMM DD, YYYY'),
            );
          }
          setIntEnableDate(
            dayjs(initialVendorData.createdDate).format('MMM DD, YYYY'),
          );
        } catch {
          // sync-log fetch is best-effort
        }
        try {
          const configResponse = await testVendorConnectionSyncAPI(
            account,
            initialVendorData,
            autoSyncAssets,
            watermarkedPhotos,
            pmsSource,
          );
          if (configResponse?.data?.status === 'Success') {
            setConnectionState(connectedConnection());
          }
        } catch (error: unknown) {
          const err = error as { response?: { status?: number } };
          if (err.response?.status) {
            setConnectionState(
              failedConnection(),
            );
            if (formSubmit) {
              dispatch(
                showAlert({
                  type: Variants.ERROR,
                  message: 'Connection Inactive. Please contact to admin.',
                }),
              );
            }
          }
        }
      }
    } finally {
      setLoading(false);
      setToggleLoader(false);
    }
  };

  // No form here: the bar's requestSubmit reaches this view through submitRef,
  // exactly as the old footer's onClick did (saveAndSync / saveWithoutSync
  // chosen by the panel).
  // Unconditional registration — see the same effect in BuildiumConnectionPanel:
  // returning early on `idle` skips the cleanup too, stranding a stale submit
  // handler across a connected/failed → idle transition (review finding 8).
  useEffect(() => {
    submitRef.current = connectionState.kind === 'idle' ? null : onSaveStored;
    return () => {
      submitRef.current = null;
    };
  }, [connectionState.kind, onSaveStored, submitRef]);

  const renderStatusPill = () => {
    switch (connectionState.kind) {
      case 'connected':
        return <StatusPill variant="success">Active</StatusPill>;
      case 'failed':
        return <StatusPill variant="destructive">Inactive</StatusPill>;
      case 'idle':
        return <StatusPill variant="muted">Not Enabled</StatusPill>;
    }
  };

  const redirectHowToConfigDetails = () => {
    redirectToHowToConfigure(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {connectionState.kind === 'idle' && (
        <>
          <div>
            <Typography variant="body">Refer to the </Typography>
            <Button type="button" variant="link" onClick={redirectHowToConfigDetails}>
              {' guide '}
            </Button>
            <Typography variant="body"> in the Integration Overview tab for step-by-step instructions.</Typography>
          </div>
          <ul className="list-disc pl-5 flex flex-col gap-2">
            <li>
              <Typography variant="body">To enable the integration, log in to your AppFolio account and search for </Typography>
              <Typography variant="body-semibold">PlanOmatic </Typography>
              <Typography variant="body">on the integrations page</Typography>
            </li>
            <li>
              <Typography variant="body">
                Once enabled, you will receive an email within 24 hours confirming the integration is live.
              </Typography>
            </li>
          </ul>
          <div>
            <Button type="button" variant="outline" onClick={() => window.open(passportUrl, '_blank', 'noopener')}>
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Connect via AppFolio
            </Button>
          </div>
        </>
      )}
      <div className="flex flex-col gap-1">
        <Typography variant="body" className="text-muted-foreground">Integration Status</Typography>
        {renderStatusPill()}
      </div>
      {connectionState.kind !== 'idle' && (
        <>
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">Integration Enabled</Typography>
            <Typography variant="body-semibold">{intEnableDate}</Typography>
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="body" className="text-muted-foreground">Recent Property Data Refresh</Typography>
            <Typography variant="body-semibold">{lastSyncDate}</Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default UserAppfolio;
